import type { NotesType, NotesVisibility } from "@scayla/api-client-fetch";
import { NotesType as NotesTypeEnum, NotesVisibility as NotesVisibilityEnum } from "@scayla/api-client-fetch";

export function isPublicNotesType(type: NotesType | undefined): boolean {
  return type === NotesTypeEnum.PUBLIC || type === NotesTypeEnum.CUSTOM_PUBLIC;
}

export function isPrivateNotesType(type: NotesType | undefined): boolean {
  return type === NotesTypeEnum.PRIVATE || type === NotesTypeEnum.CUSTOM_PRIVATE;
}

/** Public/private tabs are fixed (not user-removable), unlike ad-hoc custom notes tabs. */
export function isFixedNotesType(type: NotesType | undefined): boolean {
  return isPublicNotesType(type) || isPrivateNotesType(type);
}

/**
 * Effective visibility of a notes field block. The fixed public/private blocks carry it in the
 * stored type; only a plain custom block reads it from `settings.visibility`, where an absent
 * value means public.
 */
export function templateFieldBlockVisibility(
  settings: { type?: NotesType; visibility?: NotesVisibility } | undefined,
): "public" | "private" {
  if (isPrivateNotesType(settings?.type)) return "private";
  if (isPublicNotesType(settings?.type)) return "public";
  return settings?.visibility === NotesVisibilityEnum.PRIVATE ? "private" : "public";
}

/** The shape both the record strip and the template editor can supply for a uniqueness check. */
export interface LabelledTab {
  key: string;
  label: string;
  visibility: "public" | "private";
}

/**
 * Case-insensitive via toLocaleLowerCase (ß/Umlauts), scoped to (label, visibility). A blank
 * label never collides — it is the not-yet-named state, rejected elsewhere.
 */
export function hasDuplicateTabLabel(
  tabs: readonly LabelledTab[],
  label: string,
  visibility: "public" | "private",
  excludeKey?: string,
): boolean {
  const normalized = label.trim().toLocaleLowerCase();
  if (!normalized) return false;
  return tabs.some(
    (t) => t.key !== excludeKey && t.visibility === visibility && t.label.trim().toLocaleLowerCase() === normalized,
  );
}
